import supabase from '../supabase/supabase'

/**
 * Team Service
 * Handles team member and invitation operations via Edge Functions
 * Following the working pattern from staffService and caseService
 */

/**
 * Get all team members for current tenant
 */
export const getMembers = async () => {
  try {
    const { data, error } = await supabase.functions.invoke('api', {
      body: {
        path: 'team/members',
      },
    })

    if (error) throw new Error(error.message || 'Failed to fetch team members')

    return data.members || []
  } catch (error) {
    console.error('Error fetching team members:', error)
    throw error
  }
}

/**
 * Update team member role
 */
export const updateMemberRole = async (memberId, role) => {
  try {
    const { data, error } = await supabase.functions.invoke('api', {
      body: {
        path: 'team/members/update',
        member_id: memberId,
        role: role,
      },
    })

    if (error) throw new Error(error.message || 'Failed to update member role')

    return data
  } catch (error) {
    console.error('Error updating member role:', error)
    throw error
  }
}

/**
 * Remove team member
 */
export const removeMember = async (memberId) => {
  try {
    const { data, error } = await supabase.functions.invoke('api', {
      body: {
        path: 'team/members/delete',
        member_id: memberId,
      },
    })

    if (error) throw new Error(error.message || 'Failed to remove member')

    return data
  } catch (error) {
    console.error('Error removing member:', error)
    throw error
  }
}

/**
 * Send invitation to user
 */
export const sendInvitation = async (email, role = 'user') => {
  try {
    const { data, error } = await supabase.functions.invoke('api', {
      body: {
        path: 'team/invite',
        email: email,
        role: role,
      },
    })

    if (error) throw new Error(error.message || 'Failed to send invitation')

    return data
  } catch (error) {
    console.error('Error sending invitation:', error)
    throw error
  }
}

/**
 * Get all pending invitations for current tenant
 */
export const getInvitations = async () => {
  try {
    const { data, error } = await supabase.functions.invoke('api', {
      body: {
        path: 'team/invitations',
      },
    })

    if (error) throw new Error(error.message || 'Failed to fetch invitations')

    return data.invitations || []
  } catch (error) {
    console.error('Error fetching invitations:', error)
    throw error
  }
}

/**
 * Cancel (delete) invitation
 */
export const cancelInvitation = async (invitationId) => {
  try {
    const { data, error } = await supabase.functions.invoke('api', {
      body: {
        path: 'team/invite/cancel',
        invitation_id: invitationId,
      },
    })

    if (error) throw new Error(error.message || 'Failed to cancel invitation')

    return data
  } catch (error) {
    console.error('Error canceling invitation:', error)
    throw error
  }
}

/**
 * Resend invitation (creates new token)
 */
export const resendInvitation = async (invitationId) => {
  try {
    const { data, error } = await supabase.functions.invoke('api', {
      body: {
        path: 'team/invite/resend',
        invitation_id: invitationId,
      },
    })

    if (error) throw new Error(error.message || 'Failed to resend invitation')

    return data
  } catch (error) {
    console.error('Error resending invitation:', error)
    throw error
  }
}

/**
 * Get invitation by token (for accepting invitations)
 * This still uses direct DB access since it's for unauthenticated/different tenant users
 */
export const getInvitationByToken = async (token) => {
  try {
    const { data, error } = await supabase
      .from('invitations')
      .select(`
        *,
        tenants(id, name, slug),
        invited_by_user:auth_users!invited_by(full_name)
      `)
      .eq('token', token)
      .is('accepted_at', null)
      .single()

    if (error) throw error

    // Check if expired
    if (new Date(data.expires_at) < new Date()) {
      throw new Error('This invitation has expired')
    }

    return data
  } catch (error) {
    console.error('Error fetching invitation:', error)
    throw error
  }
}

/**
 * Accept invitation and join tenant
 * This still uses direct DB access since it's for joining a new tenant
 */
export const acceptInvitation = async (token) => {
  try {
    const { data: { user } } = await supabase.auth.getUser()

    if (!user) {
      throw new Error('You must be logged in to accept an invitation')
    }

    // Get invitation
    const invitation = await getInvitationByToken(token)

    // Verify email matches
    if (invitation.email.toLowerCase() !== user.email.toLowerCase()) {
      throw new Error('This invitation was sent to a different email address')
    }

    // Add user to tenant
    const { error: memberError } = await supabase
      .from('tenant_members')
      .insert([
        {
          tenant_id: invitation.tenants.id,
          user_id: user.id,
          role: invitation.role
        }
      ])

    if (memberError) {
      if (memberError.code === '23505') { // Unique constraint violation
        throw new Error('You are already a member of this organization')
      }
      throw memberError
    }

    // Mark invitation as accepted
    const { error: updateError } = await supabase
      .from('invitations')
      .update({ accepted_at: new Date().toISOString() })
      .eq('id', invitation.id)

    if (updateError) throw updateError

    return {
      tenant: invitation.tenants,
      role: invitation.role
    }
  } catch (error) {
    console.error('Error accepting invitation:', error)
    throw error
  }
}

export default {
  getMembers,
  updateMemberRole,
  removeMember,
  sendInvitation,
  getInvitations,
  cancelInvitation,
  resendInvitation,
  getInvitationByToken,
  acceptInvitation
}
